import styled, { css } from 'styled-components';

import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  padding: 40px;
  min-height: 100vh;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;

  margin-bottom: 32px;
`;

export const ContainerAvatar = styled.div`
  width: 72px;
  height: 72px;

  border-radius: 50%;
  border: 2px solid ${colors.primary};

  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const StudentName = styled.h1`
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${colors.onSurface};
`;

export const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

interface BadgeProps {
  level: number;
}

export const LevelBadge = styled.span<BadgeProps>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: ${radius.full};

  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  background-color: ${colors.surfaceContainerHighest};
  color: ${colors.onSurfaceVariant};

  ${({ level }) =>
    level >= 2 &&
    css`
      background-color: ${colors.primary}1a;
      color: ${colors.primary};
    `}
`;

interface StatusBadgeProps {
  status: 'active' | 'pending' | 'inactive';
}

const statusColor = {
  active: colors.primary,
  pending: colors.tertiary,
  inactive: colors.onSurfaceVariant,
};

export const StatusBadge = styled.span<StatusBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  padding: 4px 12px;
  border-radius: ${radius.full};

  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  background-color: ${({ status }) =>
    status === 'inactive'
      ? colors.surfaceContainerHighest
      : `${statusColor[status]}1a`};
  color: ${({ status }) => statusColor[status]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: currentColor;
  }
`;

interface BillingBadgeProps {
  status: 'ok' | 'pending' | 'overdue' | 'not_informed';
}

const billingColor = {
  ok: '#32a54a',
  pending: colors.onSurfaceVariant,
  overdue: colors.primary,
  not_informed: colors.onSurfaceVariant,
};

export const BillingBadge = styled.span<BillingBadgeProps>`
  display: inline-flex;
  padding: 4px 12px;
  border-radius: ${radius.full};

  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  background-color: ${({ status }) =>
    status === 'not_informed'
      ? colors.surfaceContainerHighest
      : `${billingColor[status]}1a`};
  color: ${({ status }) => billingColor[status]};
`;

export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(220px, 1fr);
  gap: 24px;
  align-items: start;

  @media (max-width: 1199px) {
    grid-template-columns: 1fr;
  }
`;

export const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

export const SummaryCard = styled.div`
  background-color: ${colors.surfaceContainerLow};
  border-radius: ${radius.lg};
  padding: 20px 24px;

  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SummaryCardRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;

  span {
    font-size: 12px;
    color: ${colors.onSurfaceVariant};
  }

  strong {
    font-size: 13px;
    color: ${colors.onSurface};
  }
`;

export const WeekBarsRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

export const WeekBarColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

interface WeekBarTrackProps {
  active: boolean;
}

export const WeekBarTrack = styled.div<WeekBarTrackProps>`
  width: 100%;
  height: 56px;
  border-radius: ${radius.sm};
  background-color: ${colors.surfaceContainerHigh};
  overflow: hidden;
  display: flex;
  align-items: flex-end;
`;

export const WeekBarFill = styled.div<{ percent: number; active: boolean }>`
  width: 100%;
  height: ${({ percent }) => Math.min(100, Math.max(4, percent))}%;
  background-color: ${({ active }) =>
    active ? colors.primary : `${colors.primary}80`};
  border-radius: ${radius.sm};
  transition: height 0.3s;
`;

export const WeekBarLabel = styled.span<{ active: boolean }>`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ active }) =>
    active ? colors.primary : colors.onSurfaceVariant};
`;

interface GaugeTrackProps {
  percent: number;
}

export const GaugeTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: ${radius.full};
  background-color: ${colors.surfaceContainerHigh};
  overflow: hidden;
  margin-top: 4px;
`;

export const GaugeFill = styled.div<GaugeTrackProps & { tone: 'ok' | 'warning' | 'danger' }>`
  height: 100%;
  width: ${({ percent }) => Math.min(100, Math.max(0, percent))}%;
  border-radius: ${radius.full};
  background-color: ${({ tone }) => {
    if (tone === 'danger') return colors.error;
    if (tone === 'warning') return colors.primary;
    return '#32a54a';
  }};
  transition: width 0.3s;
`;

export const SummaryLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.onSurfaceVariant};
`;

export const SummaryValue = styled.strong`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.onSurface};
`;

export const SectionTitle = styled.h2`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 16px;
`;

export const NavGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

export const NavCard = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;

  padding: 24px;
  background-color: ${colors.surfaceContainerLow};
  border: 1px solid transparent;
  border-radius: ${radius.lg};
  cursor: pointer;
  text-align: left;

  transition: border-color 0.2s;

  &:hover {
    border-color: ${colors.primary}4d;
  }
`;

export const NavIcon = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: ${colors.primary}1a;
  border-radius: ${radius.md};
`;

export const NavLabel = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${colors.onSurface};
`;
